import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Plus, Pencil, Trash2, GripVertical, Images, Loader2, Sparkles } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { TextF, AreaF, CheckF, ImageF, inputCls } from "./AFields";
import AICreate from "./AICreate";
import { Modal } from "./Participants";

const BLANK = { title_en: "", title_hi: "", description: "", category: "", cover_image: "", image_prompt: "", seo_title: "", seo_desc: "", published: true };

function Row({ item, onEdit, onDel, onManage, testid }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
      className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3" data-testid={`${testid}-${item.id}`}>
      <button {...attributes} {...listeners} className="cursor-grab touch-none text-slate-400 hover:text-navy" aria-label="Drag to reorder" data-testid={`${testid}-drag-${item.id}`}><GripVertical size={16} /></button>
      {item.cover_image || item.thumb || item.image
        ? <img src={item.cover_image || item.thumb || item.image} alt="" className="h-12 w-16 rounded-md object-cover" />
        : <div className="h-12 w-16 rounded-md bg-navy-50" />}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-navy">{item.title_en || item.heading || item.caption || "(untitled)"}</p>
        {item.description && <p className="truncate text-xs text-slate-500">{item.description}</p>}
      </div>
      {item.published !== undefined && <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${item.published ? "bg-brandgreen-100 text-brandgreen" : "bg-slate-100 text-slate-500"}`}>{item.published ? "LIVE" : "HIDDEN"}</span>}
      <div className="flex gap-1.5">
        {onManage && <button onClick={() => onManage(item)} className="rounded-md bg-navy px-3 py-1.5 text-xs font-bold text-white" data-testid={`album-manage-${item.id}`}><Images size={12} className="mr-1 inline" /> Images</button>}
        <button onClick={() => onEdit(item)} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`${testid}-edit-${item.id}`}><Pencil size={13} /></button>
        <button onClick={() => onDel(item)} className="rounded-md border border-gray-200 p-1.5 text-red-500 hover:border-red-400" aria-label="Delete" data-testid={`${testid}-del-${item.id}`}><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

export default function Albums() {
  const [albums, setAlbums] = useState([]);
  const [editing, setEditing] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [managing, setManaging] = useState(null);
  const [albumImages, setAlbumImages] = useState([]);
  const [allImages, setAllImages] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = () => api.get("/admin/albums").then((r) => setAlbums(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const loadAlbumImages = (a) => {
    api.get("/admin/collections/gallery?limit=100").then((r) => {
      const all = r.data.items || [];
      setAllImages(all);
      setAlbumImages(all.filter((g) => g.album_id === a.id).sort((x, y) => (x.order || 0) - (y.order || 0)));
    }).catch((e) => toast.error(errMsg(e)));
  };

  const save = async () => {
    setBusy(true);
    try { await api.post("/admin/albums/save", editing); toast.success("Album saved"); setEditing(null); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete album "${a.title_en}"? Images stay in the gallery, only the album is removed.`)) return;
    try { await api.delete(`/admin/albums/${a.id}`); toast.success("Album deleted — images preserved"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const reorderAlbums = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(albums, albums.findIndex((s) => s.id === active.id), albums.findIndex((s) => s.id === over.id));
    setAlbums(next);
    try { await api.post("/admin/albums/reorder", { ids: next.map((s) => s.id) }); toast.success("Order saved"); }
    catch (e) { toast.error(errMsg(e)); load(); }
  };

  const reorderImages = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(albumImages, albumImages.findIndex((s) => s.id === active.id), albumImages.findIndex((s) => s.id === over.id));
    setAlbumImages(next);
    try { await api.post(`/admin/albums/${managing.id}/reorder`, { ids: next.map((s) => s.id) }); toast.success("Image order saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const addPicked = async () => {
    if (!picked.length) return;
    try {
      await api.post(`/admin/albums/${managing.id}/images`, { ids: picked, action: "add" });
      toast.success(`${picked.length} image(s) added to album`);
      setPicked([]); loadAlbumImages(managing);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeImage = async (g) => {
    try { await api.post(`/admin/albums/${managing.id}/images`, { ids: [g.id], action: "remove" }); toast.success("Removed from album (image kept in gallery)"); loadAlbumImages(managing); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const setCover = async (a, url) => {
    try { await api.post("/admin/albums/save", { ...a, cover_image: url }); toast.success("Cover image set"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const available = allImages.filter((g) => g.album_id !== managing?.id);

  return (
    <div data-testid="albums-manager">
      <SEO title="Gallery Albums" />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Gallery Albums</h1>
          <p className="mt-1 text-sm text-slate-500">Group gallery images into albums. AI suggestions remain unpublished until you save and publish them.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setAiOpen(true)} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="album-ai-create-btn"><Sparkles size={15} /> Create with AI</button>
          <button onClick={() => setEditing({ ...BLANK })} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm" data-testid="album-new-btn"><Plus size={15} /> New Album</button>
        </div>
      </div>

      <div className="mt-6 max-w-3xl space-y-2">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={reorderAlbums}>
          <SortableContext items={albums.map((a) => a.id)} strategy={verticalListSortingStrategy}>
            {albums.map((a) => <Row key={a.id} item={a} testid="album-row" onEdit={(x) => setEditing({ ...BLANK, ...x })} onDel={del}
              onManage={(x) => { setManaging(x); setPicked([]); loadAlbumImages(x); }} />)}
          </SortableContext>
        </DndContext>
        {albums.length === 0 && <p className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-slate-400">No albums yet — existing gallery images keep showing as before until you create albums.</p>}
      </div>

      <AICreate entity="albums" title="Gallery Album" open={aiOpen} onClose={() => setAiOpen(false)}
        onApply={(fields) => setEditing({ ...BLANK, ...fields, cover_image: "", published: false })} />

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit Album" : "New Album"} testid="album-editor">
          <div className="space-y-5">
            <TextF label="Album Title (English)" value={editing.title_en} onChange={(v) => setEditing({ ...editing, title_en: v })} required />
            <TextF label="Album Title (Hindi)" value={editing.title_hi} onChange={(v) => setEditing({ ...editing, title_hi: v })} />
            <TextF label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} />
            <AreaF label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} rows={2} ai contentType="albums" />
            <ImageF label="Cover Image" value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} />
            <AreaF label="AI Cover Image Prompt" value={editing.image_prompt} onChange={(v) => setEditing({ ...editing, image_prompt: v })} rows={2} />
            <TextF label="SEO Title" value={editing.seo_title} onChange={(v) => setEditing({ ...editing, seo_title: v })} />
            <AreaF label="SEO Description" value={editing.seo_desc} onChange={(v) => setEditing({ ...editing, seo_desc: v })} rows={2} />
            <CheckF label="Published (visible on website)" value={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={save} disabled={busy} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="album-save">
              {busy && <Loader2 size={14} className="animate-spin" />} Save Album
            </button>
          </div>
        </Modal>
      )}

      {managing && (
        <Modal onClose={() => setManaging(null)} title={`Album Images — ${managing.title_en}`} testid="album-images-modal" wide>
          <p className="text-xs text-slate-500">Drag to reorder. Click an image below the list to set it as the album cover.</p>
          <div className="mt-4 space-y-2">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={reorderImages}>
              <SortableContext items={albumImages.map((g) => g.id)} strategy={verticalListSortingStrategy}>
                {albumImages.map((g) => (
                  <Row key={g.id} item={g} testid="album-img" onEdit={() => setCover(managing, g.image)} onDel={removeImage} />
                ))}
              </SortableContext>
            </DndContext>
            {albumImages.length === 0 && <p className="py-6 text-center text-xs text-slate-400">No images in this album yet — add below.</p>}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Tip: the pencil button on an image sets it as the album cover.</p>
          <div className="mt-5 border-t border-gray-100 pt-4">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Add images from gallery</p>
            <div className="grid max-h-56 grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-5" data-testid="album-image-picker">
              {available.map((g) => (
                <button key={g.id} onClick={() => setPicked((p) => (p.includes(g.id) ? p.filter((x) => x !== g.id) : [...p, g.id]))}
                  className={`relative overflow-hidden rounded-md border-2 ${picked.includes(g.id) ? "border-saffron-500" : "border-transparent"}`}
                  data-testid={`pick-image-${g.id}`}>
                  <img src={g.thumb || g.image} alt={g.alt_text || g.heading || ""} className="h-16 w-full object-cover" />
                  {picked.includes(g.id) && <span className="absolute inset-0 bg-saffron-500/25" />}
                </button>
              ))}
              {available.length === 0 && <p className="col-span-full py-4 text-center text-xs text-slate-400">All gallery images are already in this album.</p>}
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={addPicked} disabled={!picked.length} className="btn-primary !rounded-md !px-5 !py-2 text-xs disabled:opacity-50" data-testid="album-add-images">Add {picked.length || ""} Selected</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
